import React, { useState } from "react";
import { AddActionButton } from "../AddActionButton";
import { ReportAccordionItem } from "../ReportAccordion";
import { SelectPicker } from "../SelectPicker";
import { IconAction, StatusIcon } from "../shared/EditorControls";
import {
    CALC_OPERATORS,
    MAX_CALC_ROUND_DIGITS,
    MAX_CALC_TERMS,
    MAX_CALC_TEXT_LENGTH,
    MAX_TEXT_PARTS,
    MAX_TEXT_SEPARATOR_LENGTH,
    MAX_TEXT_STEPS,
    TEXT_STEPS,
    activeDataModels,
    calcDateColumns,
    calcTextColumns,
    calcTextOnlyColumns,
    isTextCalculatedField,
    makeTextPart,
    makeTextStep,
    textStepDef,
    withCalculatedFieldResultType,
    calcDifferenceUnits,
    calcNumberColumns,
    calculatedFieldEnglish,
    calculatedFieldInputColumns,
    calculatedFieldIssues,
    calculatedFieldSql,
    calculatedFields,
    dataColumnSelectionKey,
    isCalculatedFieldReady,
    makeCalcTerm,
    makeCalculatedField,
    metricsUsingCalculatedField,
    simulateCalculatedField,
    withoutCalculatedField,
    DATE_PARTS,
    datePartColumns,
    datePartDef,
    datePartFitsColumn,
    datePartsForColumn,
    isDatePartCalculatedField,
    isTimeOfDayColumn,
    calculatedColumnUses,
    ruleSetsUsingCalculatedField,
    columnMatchesKey,
} from "../../reportConfigurationLogic";
import type { CalcDatePartKey, CalcOperand, CalcOperandKind, CalcOperator, CalcTerm, CalcTextPart, CalcTextStep, CalcTextStepKind, CalculatedField, CalculatedFieldResultType, DataColumn, ModelKey, ReportConfig } from "../../types";

// The "Calculated Fields" tab: reusable per-row numbers (logic/calculatedFields.ts). A field
// is defined once here and then used by metrics - as a column on a Full Row Detail metric,
// or as the Sum / Average / Min / Max a Count or Percentage metric reports.
//
// Layout of a field:
//   - Name, data model (only when the report has several), usage, delete.
//   - Formula: one row per value - Operation | Value Type | Value - plus "Add Step".
//   - Options: rounding, blank values, dividing by zero.
//   - Result: the formula read back in plain English (green when complete, amber with the
//     list of what is missing when not), a "Try It" calculator and the SQL on demand.
//
// A field takes columns from ONE data model and only metrics on that model can use it. With
// several models attached, fields are listed under their model and created from there, and
// switching a field's model asks first because it clears the formula.
//
// The formula is a chain of pickers, never free text: every operand is a catalog column or a
// validated number, so nothing typed here reaches SQL as-is.
//
// A field's Result is a Number (the formula above) or Text. A text field is built from pieces
// - a column read as text, or typed text - each run through string steps (trim, change case,
// extract, replace, pad, mask, format, ...) and joined with an optional separator. Typed text
// in a step is bound as a parameter on the server (logic/textCalculatedFields.ts). Text fields
// are Full Row Detail columns; only number fields can be a metric's Measure.
//
// A Date Part field takes one part of a date / time column - Day Name, Month Name, Year,
// Week Of Month, Start Of Week, Hour ... (logic/dateParts.ts). No formula, no parameters.
//
// Every complete field is also a column of its data model (logic/calculatedColumns.ts): it is
// listed as "<name> (Calculated)" wherever a metric breakdown, an include / exclude list, a
// filter or a Categorization condition picks a column.

type CalculatedFieldsEditorProps = {
    report: ReportConfig;
    editing: {
        replaceSelectedReport: (updater: (report: ReportConfig) => ReportConfig) => void;
        revealAccordionItem: (id: string) => void;
    };
};

const OPERAND_KIND_OPTIONS: { id: CalcOperandKind; value: CalcOperandKind; name: string }[] = [
    { id: "column", value: "column", name: "Number Column" },
    { id: "number", value: "number", name: "A Number" },
    { id: "minutes_between", value: "minutes_between", name: "Time Between Two Dates" },
    { id: "text_length", value: "text_length", name: "Length Of Text" },
    { id: "text_position", value: "text_position", name: "Position Of Text" },
    { id: "text_number", value: "text_number", name: "Text As A Number" },
];

const RESULT_TYPE_OPTIONS: { id: CalculatedFieldResultType; value: CalculatedFieldResultType; name: string }[] = [
    { id: "number", value: "number", name: "Number" },
    { id: "text", value: "text", name: "Text" },
    { id: "datePart", value: "datePart", name: "Date Part (Day, Month, Year ...)" },
];

const RESULT_TYPE_NAMES: Record<CalculatedFieldResultType, string> = { number: "Number", text: "Text", datePart: "Date Part" };

const VALUE_TYPE_NAMES: Record<string, string> = { number: "a whole number", text: "text", date: "a date" };

const resultTypeOf = (field: CalculatedField): CalculatedFieldResultType => field.resultType === "text" || field.resultType === "datePart" ? field.resultType : "number";

const SOURCE_KIND_OPTIONS = [
    { id: "column", value: "column", name: "Column" },
    { id: "text", value: "text", name: "Typed Text" },
];

const STEP_OPTIONS = TEXT_STEPS.map((step) => ({ id: step.key, value: step.key, name: `${step.group} · ${step.label}` }));

const OPERATOR_OPTIONS = CALC_OPERATORS.map((operator) => ({ id: operator.key, value: operator.key, name: operator.label }));

const ROUND_OPTIONS = [
    { id: "none", value: "none", name: "No Rounding" },
    ...Array.from({ length: MAX_CALC_ROUND_DIGITS + 1 }, (_, digits) => ({ id: String(digits), value: String(digits), name: digits === 0 ? "Whole Number" : `${digits} Decimal${digits === 1 ? "" : "s"}` })),
];

const BLANK_OPTIONS = [
    { id: "blank", value: "blank", name: "Make The Result Blank" },
    { id: "zero", value: "zero", name: "Count As 0" },
];

const DIVIDE_BY_ZERO_OPTIONS = [
    { id: "blank", value: "blank", name: "Show Blank" },
    { id: "zero", value: "zero", name: "Show 0" },
];

const UNIT_LABELS: Record<string, string> = { minute: "Minutes", hour: "Hours", day: "Days" };

function emptyOperand(kind: CalcOperandKind): CalcOperand {
    if (kind === "number") return { kind: "number", value: "" };
    if (kind === "minutes_between") return { kind: "minutes_between", fromColumn: "", toColumn: "", differenceUnit: "minute", absolute: false };
    if (kind === "text_length") return { kind: "text_length", column: "" };
    if (kind === "text_position") return { kind: "text_position", column: "", value: "" };
    if (kind === "text_number") return { kind: "text_number", column: "" };
    return { kind: "column", column: "" };
}

function columnOptions(columns: DataColumn[]) {
    return columns.map((column) => ({ id: dataColumnSelectionKey(column) || column.key, value: dataColumnSelectionKey(column) || column.key, name: column.label }));
}

function usesColumns(field: CalculatedField) {
    if (isDatePartCalculatedField(field)) return !!field.dateColumn;
    if (isTextCalculatedField(field)) return (field.textParts || []).some((part) => part.sourceKind === "column" && !!part.column);
    return field.terms.some((term) => (term.operand.kind !== "number" && term.operand.kind !== "minutes_between" && !!term.operand.column)
        || (term.operand.kind === "minutes_between" && (term.operand.fromColumn || term.operand.toColumn)));
}

/** True when switching the result type would throw away something the user built. */
function hasFormulaContent(field: CalculatedField) {
    if (isDatePartCalculatedField(field)) return !!field.dateColumn;
    if (isTextCalculatedField(field)) return (field.textParts || []).some((part) => !!part.column || !!part.value || part.steps.length > 0);
    return field.terms.length > 1 || usesColumns(field) || (field.terms[0]?.operand.kind === "number" && !!field.terms[0].operand.value);
}

function nextFieldName(fields: CalculatedField[]) {
    const names = new Set(fields.map((field) => (field.name || "").trim().toLowerCase()));
    let index = fields.length + 1;
    while (names.has(`calculated field ${index}`)) index++;
    return `Calculated Field ${index}`;
}

export function CalculatedFieldsEditor({ report, editing }: CalculatedFieldsEditorProps) {
    const { replaceSelectedReport, revealAccordionItem } = editing;
    const [openFieldId, setOpenFieldId] = useState("");
    const [pendingModel, setPendingModel] = useState<Record<string, ModelKey>>({});
    const [pendingType, setPendingType] = useState<Record<string, CalculatedFieldResultType | "">>({});
    const [sqlOpen, setSqlOpen] = useState<Record<string, boolean>>({});
    const [samples, setSamples] = useState<Record<string, Record<string, string>>>({});
    const models = activeDataModels();
    const fields = calculatedFields(report);
    const multiModel = report.models.length > 1;
    const modelLabel = (modelKey: ModelKey | "") => (modelKey && (models[modelKey]?.label || modelKey)) || "No Data Model";

    const updateField = (fieldId: string, updater: (field: CalculatedField) => CalculatedField) =>
        replaceSelectedReport((current) => ({ ...current, calculatedFields: calculatedFields(current).map((field) => field.id === fieldId ? updater(field) : field) }));
    const updateTerm = (fieldId: string, termId: string, updater: (term: CalcTerm) => CalcTerm) =>
        updateField(fieldId, (field) => ({ ...field, terms: field.terms.map((term) => term.id === termId ? updater(term) : term) }));

    function addField(modelKey: ModelKey) {
        const field = makeCalculatedField(modelKey, nextFieldName(fields));
        setOpenFieldId(field.id);
        replaceSelectedReport((current) => ({ ...current, calculatedFields: [...calculatedFields(current), field] }));
        revealAccordionItem(`calc-${field.id}`);
    }

    // ---- Formula rows ----------------------------------------------------------------

    function renderOperand(field: CalculatedField, term: CalcTerm) {
        const operand = term.operand;
        const setOperand = (next: CalcOperand) => updateTerm(field.id, term.id, (current) => ({ ...current, operand: next }));
        if (operand.kind === "number") {
            return (
                <input
                    className="rc-calc-number"
                    inputMode="decimal"
                    placeholder="e.g. 60 or 1.5"
                    value={operand.value}
                    onChange={(event) => setOperand({ kind: "number", value: event.target.value.trim() })}
                />
            );
        }
        if (operand.kind === "column") {
            const options = columnOptions(calcNumberColumns(field.model));
            return (
                <SelectPicker
                    options={options}
                    value={options.some((option) => option.value === operand.column) ? operand.column : ""}
                    placeholder={options.length ? "Choose A Number Column..." : `No Number Columns On ${modelLabel(field.model)}`}
                    searchPlaceholder="Search Columns..."
                    onChange={(value) => setOperand({ kind: "column", column: value })}
                />
            );
        }
        if (operand.kind === "text_length" || operand.kind === "text_position" || operand.kind === "text_number") {
            const textOptions = columnOptions(operand.kind === "text_number" ? calcTextOnlyColumns(field.model) : calcTextColumns(field.model));
            const picker = (
                <SelectPicker
                    options={textOptions}
                    value={textOptions.some((option) => option.value === operand.column) ? operand.column : ""}
                    placeholder={textOptions.length ? (operand.kind === "text_number" ? "Choose A Text Column..." : "Choose A Column...") : `No Usable Columns On ${modelLabel(field.model)}`}
                    searchPlaceholder="Search Columns..."
                    onChange={(value) => setOperand({ ...operand, column: value })}
                />
            );
            if (operand.kind !== "text_position") return picker;
            return (
                <div className="rc-calc-between">
                    <input
                        className="rc-calc-text"
                        maxLength={MAX_CALC_TEXT_LENGTH}
                        placeholder="Text To Find"
                        aria-label="Text to find"
                        value={operand.value}
                        onChange={(event) => setOperand({ ...operand, value: event.target.value })}
                    />
                    <span className="rc-calc-between-word">in</span>
                    {picker}
                </div>
            );
        }
        const options = columnOptions(calcDateColumns(field.model));
        const units = calcDifferenceUnits(field, operand);
        const unit = units.includes(operand.differenceUnit || "minute") ? operand.differenceUnit || "minute" : units[units.length - 1] || "minute";
        return (
            <div className="rc-calc-between">
                <SelectPicker options={options} value={operand.fromColumn} placeholder="From Date..." searchPlaceholder="Search Columns..." onChange={(value) => setOperand({ ...operand, fromColumn: value })} />
                <span className="rc-calc-between-word">to</span>
                <SelectPicker options={options} value={operand.toColumn} placeholder="To Date..." searchPlaceholder="Search Columns..." onChange={(value) => setOperand({ ...operand, toColumn: value })} />
                <span className="rc-calc-between-word">in</span>
                <SelectPicker options={units.map((item) => ({ id: item, value: item, name: UNIT_LABELS[item] }))} value={unit} searchable={false} onChange={(value) => setOperand({ ...operand, differenceUnit: value as "minute" | "hour" | "day" })} />
                <label className="rc-cb" title="Always a positive number, whichever date is later">
                    <input type="checkbox" checked={!!operand.absolute} onChange={(event) => setOperand({ ...operand, absolute: event.target.checked })} /> Ignore which comes first
                </label>
            </div>
        );
    }

    function renderTerm(field: CalculatedField, term: CalcTerm, index: number) {
        return (
            <div className="rc-calc-row" key={term.id} role="row">
                <span className="rc-calc-row-index" role="cell">{index + 1}</span>
                <span className="rc-calc-row-op" role="cell">
                    {index === 0
                        ? <span className="rc-calc-start">Start With</span>
                        : <SelectPicker options={OPERATOR_OPTIONS} value={term.operator || ""} placeholder="Operation..." searchable={false} onChange={(value) => updateTerm(field.id, term.id, (current) => ({ ...current, operator: value as CalcOperator }))} />}
                </span>
                <span className="rc-calc-row-kind" role="cell">
                    <SelectPicker options={OPERAND_KIND_OPTIONS} value={term.operand.kind} searchable={false} onChange={(value) => {
                        if (value !== term.operand.kind) updateTerm(field.id, term.id, (current) => ({ ...current, operand: emptyOperand(value as CalcOperandKind) }));
                    }} />
                </span>
                <span className="rc-calc-row-value" role="cell">{renderOperand(field, term)}</span>
                <span className="rc-calc-row-remove" role="cell">
                    {field.terms.length > 1 && (
                        <IconAction label={`Remove Value ${index + 1}`} icon="close" onClick={() => updateField(field.id, (current) => {
                            const terms = current.terms.filter((item) => item.id !== term.id);
                            // The new first value has nothing to its left, so it loses its operation.
                            return { ...current, terms: terms.map((item, position) => position === 0 ? { id: item.id, operand: item.operand } : item) };
                        })} />
                    )}
                </span>
            </div>
        );
    }

    // ---- Text fields: pieces and their steps ------------------------------------------

    const updatePart = (fieldId: string, partId: string, updater: (part: CalcTextPart) => CalcTextPart) =>
        updateField(fieldId, (field) => ({ ...field, textParts: (field.textParts || []).map((part) => part.id === partId ? updater(part) : part) }));
    const updateStep = (fieldId: string, partId: string, stepId: string, updater: (step: CalcTextStep) => CalcTextStep) =>
        updatePart(fieldId, partId, (part) => ({ ...part, steps: part.steps.map((step) => step.id === stepId ? updater(step) : step) }));
    const moveItem = <T,>(items: T[], index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= items.length) return items;
        const next = [...items];
        [next[index], next[target]] = [next[target], next[index]];
        return next;
    };

    function renderStepParams(field: CalculatedField, part: CalcTextPart, step: CalcTextStep) {
        const def = textStepDef(step.kind);
        if (!def) return null;
        const set = (patch: Partial<CalcTextStep>) => updateStep(field.id, part.id, step.id, (current) => ({ ...current, ...patch }));
        const numberInput = (key: "number" | "number2", param: NonNullable<typeof def.number>) => (
            <label className="rc-calc-param" key={key}>
                <span>{param.label}</span>
                <input
                    className="rc-calc-count"
                    type="number"
                    min={param.min}
                    max={param.max}
                    value={step[key] ?? ""}
                    onChange={(event) => set({ [key]: event.target.value === "" ? null : Number(event.target.value) })}
                />
            </label>
        );
        const textInput = (key: "text" | "text2", param: NonNullable<typeof def.text>) => (
            <label className="rc-calc-param" key={key}>
                <span>{param.label}</span>
                <input
                    className={param.maxLength === 1 ? "rc-calc-char" : "rc-calc-text"}
                    maxLength={param.maxLength ?? MAX_CALC_TEXT_LENGTH}
                    placeholder={param.placeholder}
                    value={step[key] ?? ""}
                    // Not trimmed: a space is a meaningful separator, prefix or padding character.
                    onChange={(event) => set({ [key]: event.target.value })}
                />
            </label>
        );
        return (
            <>
                {def.number && numberInput("number", def.number)}
                {def.number2 && numberInput("number2", def.number2)}
                {def.text && textInput("text", def.text)}
                {def.text2 && textInput("text2", def.text2)}
                {def.styles && (
                    <label className="rc-calc-param">
                        <span>Format</span>
                        <SelectPicker options={def.styles.map((style) => ({ id: style.key, value: style.key, name: style.label }))} value={step.style || ""} placeholder="Format..." searchable={false} onChange={(value) => set({ style: value })} />
                    </label>
                )}
            </>
        );
    }

    function renderTextPart(field: CalculatedField, part: CalcTextPart, index: number, count: number) {
        const parts = field.textParts || [];
        const columns = columnOptions(calcTextColumns(field.model));
        return (
            <div className="rc-calc-piece" key={part.id}>
                <div className="rc-calc-piece-head">
                    <span className="rc-calc-row-index">{index + 1}</span>
                    <SelectPicker
                        ariaLabel={`Piece ${index + 1} source`}
                        options={SOURCE_KIND_OPTIONS}
                        value={part.sourceKind}
                        searchable={false}
                        onChange={(value) => {
                            if (value !== part.sourceKind) updatePart(field.id, part.id, (current) => ({ ...makeTextPart(value as CalcTextPart["sourceKind"]), id: current.id, steps: current.steps }));
                        }}
                    />
                    <span className="rc-calc-piece-source">
                        {part.sourceKind === "text" ? (
                            <input
                                className="rc-calc-text"
                                maxLength={MAX_CALC_TEXT_LENGTH}
                                placeholder='e.g. ", " or "ID-"'
                                aria-label={`Piece ${index + 1} text`}
                                value={part.value ?? ""}
                                onChange={(event) => updatePart(field.id, part.id, (current) => ({ ...current, value: event.target.value }))}
                            />
                        ) : (
                            <SelectPicker
                                options={columns}
                                value={columns.some((option) => option.value === part.column) ? part.column || "" : ""}
                                placeholder={columns.length ? "Choose A Column..." : `No Usable Columns On ${modelLabel(field.model)}`}
                                searchPlaceholder="Search Columns..."
                                onChange={(value) => updatePart(field.id, part.id, (current) => ({ ...current, column: value }))}
                            />
                        )}
                    </span>
                    <button type="button" className="rc-minibtn" aria-label={`Move piece ${index + 1} up`} disabled={index === 0} onClick={() => updateField(field.id, (current) => ({ ...current, textParts: moveItem(current.textParts || [], index, -1) }))}>Up</button>
                    <button type="button" className="rc-minibtn" aria-label={`Move piece ${index + 1} down`} disabled={index === count - 1} onClick={() => updateField(field.id, (current) => ({ ...current, textParts: moveItem(current.textParts || [], index, 1) }))}>Down</button>
                    {count > 1 && <IconAction label={`Remove Piece ${index + 1}`} icon="close" onClick={() => updateField(field.id, (current) => ({ ...current, textParts: (current.textParts || []).filter((item) => item.id !== part.id) }))} />}
                </div>
                {part.steps.length > 0 && (
                    <ol className="rc-calc-steps" aria-label={`Piece ${index + 1} steps`}>
                        {part.steps.map((step, stepIndex) => (
                            <li className="rc-calc-step" key={step.id}>
                                <span className="rc-calc-step-index">{stepIndex + 1}</span>
                                <span className="rc-calc-step-kind">
                                    <SelectPicker
                                        ariaLabel={`Step ${stepIndex + 1}`}
                                        options={STEP_OPTIONS}
                                        value={step.kind}
                                        searchPlaceholder="Search Steps..."
                                        onChange={(value) => {
                                            if (value !== step.kind) updateStep(field.id, part.id, step.id, () => ({ ...makeTextStep(value as CalcTextStepKind), id: step.id }));
                                        }}
                                    />
                                </span>
                                <span className="rc-calc-step-params">{renderStepParams(field, part, step)}</span>
                                <span className="rc-calc-step-actions">
                                    <button type="button" className="rc-minibtn" aria-label={`Move step ${stepIndex + 1} up`} disabled={stepIndex === 0} onClick={() => updatePart(field.id, part.id, (current) => ({ ...current, steps: moveItem(current.steps, stepIndex, -1) }))}>Up</button>
                                    <button type="button" className="rc-minibtn" aria-label={`Move step ${stepIndex + 1} down`} disabled={stepIndex === part.steps.length - 1} onClick={() => updatePart(field.id, part.id, (current) => ({ ...current, steps: moveItem(current.steps, stepIndex, 1) }))}>Down</button>
                                    <IconAction label={`Remove Step ${stepIndex + 1}`} icon="close" onClick={() => updatePart(field.id, part.id, (current) => ({ ...current, steps: current.steps.filter((item) => item.id !== step.id) }))} />
                                </span>
                                <span className="rc-hint rc-calc-step-hint">{textStepDef(step.kind)?.hint}</span>
                            </li>
                        ))}
                    </ol>
                )}
                <AddActionButton
                    className="compact"
                    disabled={part.steps.length >= MAX_TEXT_STEPS}
                    title={part.steps.length >= MAX_TEXT_STEPS ? `A piece can have at most ${MAX_TEXT_STEPS} steps.` : undefined}
                    onClick={() => updatePart(field.id, part.id, (current) => ({ ...current, steps: [...current.steps, makeTextStep("trim")] }))}
                >
                    Add Step
                </AddActionButton>
            </div>
        );
    }

    function renderTextBuilder(field: CalculatedField) {
        const parts = field.textParts || [];
        return (
            <section className="rc-calc-section">
                <div className="rc-calc-section-head">
                    <span className="rc-lbl">Text</span>
                    <span className="rc-hint">Each piece starts from a column or typed text, then its steps run in order. Pieces are joined in order.{multiModel ? ` Columns come from ${modelLabel(field.model)} only.` : ""} Matching (replace, text before / after, part) ignores upper and lower case.</span>
                </div>
                <div className="rc-calc-pieces">{parts.map((part, index) => renderTextPart(field, part, index, parts.length))}</div>
                <div className="rc-calc-options">
                    <AddActionButton
                        className="compact"
                        disabled={parts.length >= MAX_TEXT_PARTS}
                        title={parts.length >= MAX_TEXT_PARTS ? `A text field can have at most ${MAX_TEXT_PARTS} pieces.` : undefined}
                        onClick={() => updateField(field.id, (current) => ({ ...current, textParts: [...(current.textParts || []), makeTextPart()] }))}
                    >
                        Add Piece
                    </AddActionButton>
                    {parts.length > 1 && (
                        <label className="rc-calc-param">
                            <span>Separator Between Pieces</span>
                            <input
                                className="rc-calc-text"
                                maxLength={MAX_TEXT_SEPARATOR_LENGTH}
                                placeholder="(none - joined directly)"
                                value={field.textSeparator ?? ""}
                                onChange={(event) => updateField(field.id, (current) => ({ ...current, textSeparator: event.target.value }))}
                            />
                        </label>
                    )}
                    {parts.length > 1 && (field.textSeparator ?? "") !== "" && <span className="rc-hint">Blank pieces are skipped, so no doubled separators.</span>}
                </div>
            </section>
        );
    }

    // ---- Date part fields ----------------------------------------------------------------

    function renderDatePartBuilder(field: CalculatedField) {
        const columns = datePartColumns(field.model);
        const columnOptionsList = columnOptions(columns);
        const column = field.dateColumn ? columns.find((item) => columnMatchesKey(item, field.dateColumn || "")) || null : null;
        const parts = datePartsForColumn(column);
        const partGroups: Record<string, string> = {};
        parts.forEach((part) => { partGroups[part.key] = part.group; });
        const part = datePartDef(field.datePart);
        const fits = !column || !part || datePartFitsColumn(part, column);
        return (
            <section className="rc-calc-section">
                <div className="rc-calc-section-head">
                    <span className="rc-lbl">Date Part</span>
                    <span className="rc-hint">Takes one part of a date for every record. Weeks start on Sunday (except the ISO week), and day and month names are always in English, whatever the database settings.{multiModel ? ` Columns come from ${modelLabel(field.model)} only.` : ""}</span>
                </div>
                <div className="rc-calc-options">
                    <label className="rc-label-block">
                        Date Column
                        <SelectPicker
                            options={columnOptionsList}
                            value={columnOptionsList.some((option) => option.value === field.dateColumn) ? field.dateColumn || "" : ""}
                            placeholder={columnOptionsList.length ? "Choose A Date Column..." : `No Date Columns On ${modelLabel(field.model)}`}
                            searchPlaceholder="Search Columns..."
                            onChange={(value) => updateField(field.id, (current) => {
                                const next = columns.find((item) => columnMatchesKey(item, value)) || null;
                                // A time-of-day column has no date parts: move to Hour Of Day rather than keep a part it can't give.
                                const keep = datePartFitsColumn(datePartDef(current.datePart), next);
                                return { ...current, dateColumn: value, datePart: keep ? current.datePart : (isTimeOfDayColumn(next) ? "hour" : "day_name") };
                            })}
                        />
                    </label>
                    <label className="rc-label-block">
                        Part
                        <SelectPicker
                            options={parts.map((item) => ({ id: item.key, value: item.key, name: item.label }))}
                            optionGroupLabels={partGroups}
                            value={field.datePart || ""}
                            placeholder="Choose A Part..."
                            searchPlaceholder="Search Parts..."
                            onChange={(value) => updateField(field.id, (current) => ({ ...current, datePart: value as CalcDatePartKey }))}
                        />
                    </label>
                </div>
                {part && (
                    <p className="rc-hint">
                        <b>{part.label}</b>: {part.example}. The result is {VALUE_TYPE_NAMES[part.valueType] || "text"}
                        {part.valueType === "number" ? " - filter it with equals, greater than, between ..." : part.valueType === "date" ? " - filter it with before, after, between ..." : ""}.
                        {!fits && column ? ` ${column.label} can't give this part - choose another part or column.` : ""}
                    </p>
                )}
                {!part && <p className="rc-hint">{DATE_PARTS.length} parts available: year, quarter, month, week, day, time of day and period start / end dates.</p>}
            </section>
        );
    }

    // ---- One field ---------------------------------------------------------------------

    function renderField(field: CalculatedField) {
        const issues = calculatedFieldIssues(report, field);
        const english = calculatedFieldEnglish(field);
        const sql = calculatedFieldSql(report, field);
        const ready = !issues.length && !!sql;
        const users = metricsUsingCalculatedField(report, field.id);
        const hasDivision = field.terms.some((term) => term.operator === "divide" || term.operator === "mod");
        const pending = pendingModel[field.id];
        const inputs = calculatedFieldInputColumns(field);
        const values = samples[field.id] || {};
        const dateKeys = new Set(calcDateColumns(field.model).map((column) => column.key));
        const isText = isTextCalculatedField(field);
        const isDatePart = isDatePartCalculatedField(field);
        const resultType = resultTypeOf(field);
        const columnUses = calculatedColumnUses(report, field.id);
        const ruleSetUsers = ruleSetsUsingCalculatedField(report, field.id);
        const typePending = pendingType[field.id] || "";
        const applyResultType = (resultType: CalculatedFieldResultType) => {
            replaceSelectedReport((current) => ({
                ...current,
                // Only a number field can be a Measure: metrics adding this field up go back to counting rows.
                sections: resultType !== "number" ? current.sections.map((section) => section.measure?.fieldId === field.id ? { ...section, measure: null } : section) : current.sections,
                calculatedFields: calculatedFields(current).map((item) => item.id === field.id ? withCalculatedFieldResultType(item, resultType) : item),
            }));
            setPendingType((current) => ({ ...current, [field.id]: "" }));
        };
        const measureUsers = users.filter((section) => section.measure?.fieldId === field.id);
        const chooseResultType = (value: string) => {
            const next = value as CalculatedFieldResultType;
            if (next === resultType) return;
            if (hasFormulaContent(field) || (next !== "number" && measureUsers.length) || columnUses.length) setPendingType((current) => ({ ...current, [field.id]: next }));
            else applyResultType(next);
        };

        const chooseModel = (modelKey: string) => {
            if (modelKey === field.model) return;
            if (usesColumns(field)) setPendingModel((current) => ({ ...current, [field.id]: modelKey }));
            else updateField(field.id, (current) => ({ ...current, model: modelKey }));
        };
        const confirmModel = () => {
            const firstTerm = makeCalcTerm();
            replaceSelectedReport((current) => {
                // Metrics on the old model can no longer use the field: drop their references, keep the field.
                const cleaned = users.length || ruleSetUsers.length ? { ...withoutCalculatedField(current, field.id), calculatedFields: calculatedFields(current) } : current;
                // Every column the field read belongs to the old model, so its definition starts over.
                const reset = (item: CalculatedField): CalculatedField => isDatePartCalculatedField(item)
                    ? { ...item, model: pending, dateColumn: "" }
                    : isTextCalculatedField(item)
                        ? { ...item, model: pending, textParts: [makeTextPart()] }
                        : { ...item, model: pending, terms: [firstTerm] };
                return { ...cleaned, calculatedFields: calculatedFields(cleaned).map((item) => item.id === field.id ? reset(item) : item) };
            });
            setPendingModel((current) => ({ ...current, [field.id]: "" }));
        };

        return (
            <div className="rc-calc-card" data-rc-validation={`calc-field-${field.id}`}>
                <div className="rc-calc-header">
                    <label className="rc-calc-header-field">
                        <span>Field Name</span>
                        <input value={field.name} maxLength={128} placeholder="e.g. Cost Per Mile" onChange={(event) => updateField(field.id, (current) => ({ ...current, name: event.target.value }))} />
                    </label>
                    {multiModel && (
                        <label className="rc-calc-header-field">
                            <span>Data Model</span>
                            <SelectPicker
                                options={report.models.map((modelKey) => ({ id: modelKey, value: modelKey, name: modelLabel(modelKey) }))}
                                value={pending || field.model}
                                placeholder="Choose A Data Model..."
                                searchable={false}
                                onChange={chooseModel}
                            />
                        </label>
                    )}
                    <label className="rc-calc-header-field">
                        <span>Result</span>
                        <SelectPicker options={RESULT_TYPE_OPTIONS} value={typePending || resultType} searchable={false} onChange={chooseResultType} />
                    </label>
                    <span className="rc-badge">{users.length} Metric{users.length === 1 ? "" : "s"} Using This</span>
                    {ruleSetUsers.length > 0 && <span className="rc-badge">{ruleSetUsers.length} Categorization{ruleSetUsers.length === 1 ? "" : "s"}</span>}
                    <span className="rc-spacer" />
                    <IconAction
                        label={users.length || ruleSetUsers.length
                            ? `Delete Field (also removes it from ${[users.length ? `${users.length} metric${users.length === 1 ? "" : "s"}` : "", ruleSetUsers.length ? `${ruleSetUsers.length} categorization${ruleSetUsers.length === 1 ? "" : "s"}` : ""].filter(Boolean).join(" and ")}; breakdowns on it are cleared)`
                            : "Delete Field"}
                        onClick={() => replaceSelectedReport((current) => withoutCalculatedField(current, field.id))}
                    />
                </div>
                {typePending && (
                    <div className="rc-calc-callout warning" role="alert">
                        <span>Switching to a <b>{RESULT_TYPE_NAMES[typePending]}</b> result clears this {isText ? "text" : isDatePart ? "date part" : "formula"}{typePending !== "number" && measureUsers.length ? ` and sets ${measureUsers.length} metric${measureUsers.length === 1 ? "" : "s"} adding it up back to counting rows` : ""}.{columnUses.length ? ` It is also used as a column (${columnUses.map((use) => `${use.ownerName}: ${use.where}`).join("; ")}) - check those values and conditions still fit the new result.` : ""}</span>
                        <button type="button" className="rc-minibtn" onClick={() => applyResultType(typePending)}>Switch And Clear</button>
                        <button type="button" className="rc-minibtn" onClick={() => setPendingType((current) => ({ ...current, [field.id]: "" }))}>Cancel</button>
                    </div>
                )}
                {pending && (
                    <div className="rc-calc-callout warning" role="alert">
                        <span>Switching to <b>{modelLabel(pending)}</b> clears this {isDatePart ? "date part" : isText ? "text" : "formula"}{users.length ? ` and removes the field from ${users.length} ${modelLabel(field.model)} metric${users.length === 1 ? "" : "s"}` : ""}{ruleSetUsers.length ? `${users.length ? " and" : " and removes"} its conditions from ${ruleSetUsers.length} categorization${ruleSetUsers.length === 1 ? "" : "s"}` : ""}.</span>
                        <button type="button" className="rc-minibtn" onClick={confirmModel}>Switch And Clear</button>
                        <button type="button" className="rc-minibtn" onClick={() => setPendingModel((current) => ({ ...current, [field.id]: "" }))}>Cancel</button>
                    </div>
                )}

                {isDatePart ? renderDatePartBuilder(field) : isText ? renderTextBuilder(field) : (<>
                <section className="rc-calc-section">
                    <div className="rc-calc-section-head">
                        <span className="rc-lbl">Formula</span>
                        <span className="rc-hint">Multiplication, division and remainder (mod) are worked out before addition and subtraction.{multiModel ? ` Columns come from ${modelLabel(field.model)} only.` : ""}</span>
                    </div>
                    <div className="rc-calc-table" role="table" aria-label="Formula">
                        <div className="rc-calc-row rc-calc-row-head" role="row">
                            <span role="columnheader">#</span>
                            <span role="columnheader">Operation</span>
                            <span role="columnheader">Value Type</span>
                            <span role="columnheader">Value</span>
                            <span role="columnheader" aria-label="Remove" />
                        </div>
                        {field.terms.map((term, index) => renderTerm(field, term, index))}
                    </div>
                    <AddActionButton
                        className="compact"
                        disabled={field.terms.length >= MAX_CALC_TERMS}
                        title={field.terms.length >= MAX_CALC_TERMS ? `A formula can have at most ${MAX_CALC_TERMS} values.` : undefined}
                        onClick={() => updateField(field.id, (current) => ({ ...current, terms: [...current.terms, makeCalcTerm("divide")] }))}
                    >
                        Add Step
                    </AddActionButton>
                </section>

                <section className="rc-calc-section">
                    <div className="rc-calc-section-head"><span className="rc-lbl">Options</span></div>
                    <div className="rc-calc-options">
                        <label className="rc-label-block">
                            Round To
                            <SelectPicker options={ROUND_OPTIONS} value={field.roundTo === null || field.roundTo === undefined ? "none" : String(field.roundTo)} searchable={false} onChange={(value) => updateField(field.id, (current) => ({ ...current, roundTo: value === "none" ? null : Number(value) }))} />
                        </label>
                        <label className="rc-label-block">
                            Blank Values
                            <SelectPicker options={BLANK_OPTIONS} value={field.blankAs || "blank"} searchable={false} onChange={(value) => updateField(field.id, (current) => ({ ...current, blankAs: value as "blank" | "zero" }))} />
                        </label>
                        {hasDivision && (
                            <label className="rc-label-block">
                                If Dividing By Zero
                                <SelectPicker options={DIVIDE_BY_ZERO_OPTIONS} value={field.divideByZero || "blank"} searchable={false} onChange={(value) => updateField(field.id, (current) => ({ ...current, divideByZero: value as "blank" | "zero" }))} />
                            </label>
                        )}
                    </div>
                </section>
                </>)}

                <div className={ready ? "rc-calc-result ready" : "rc-calc-result"}>
                    <span className="material-icons md-18" aria-hidden="true">{ready ? "check_circle" : "error_outline"}</span>
                    <div className="rc-calc-result-body">
                        <p className="rc-calc-reads"><span className="rc-calc-reads-label">Reads As</span> <b>{(field.name || "").trim() || "This Field"}</b> = {english || "(not finished yet)"}</p>
                        {!ready && issues.length > 0 && <ul className="rc-calc-issues">{issues.map((issue) => <li key={issue}>{issue}</li>)}</ul>}
                    </div>
                    {sql && (
                        <button type="button" className="rc-minibtn" onClick={() => setSqlOpen((current) => ({ ...current, [field.id]: !current[field.id] }))}>
                            {sqlOpen[field.id] ? "Hide SQL" : "Show SQL"}
                        </button>
                    )}
                </div>
                {sql && sqlOpen[field.id] && <pre className="rc-calc-sql"><code>{sql}</code></pre>}

                {ready && (
                    <section className="rc-calc-section">
                        <div className="rc-calc-section-head">
                            <span className="rc-lbl">Try It</span>
                            <span className="rc-hint">Enter sample values to check the result before using the field in a metric.</span>
                        </div>
                        <div className="rc-calc-try">
                            {inputs.map((column) => (
                                <label className="rc-label-block" key={column.key}>
                                    {column.label}
                                    <input
                                        value={values[column.key] || ""}
                                        placeholder={dateKeys.has(column.key) ? "YYYY-MM-DD HH:mm" : isText || !calcNumberColumns(field.model).some((item) => item.key === column.key) ? "Sample Text" : "Number"}
                                        onChange={(event) => setSamples((current) => ({ ...current, [field.id]: { ...(current[field.id] || {}), [column.key]: event.target.value } }))}
                                    />
                                </label>
                            ))}
                            <span className="rc-calc-try-equals" aria-hidden="true">=</span>
                            <span className="rc-calc-try-result" aria-live="polite">{inputs.some((column) => values[column.key]) || !inputs.length ? simulateCalculatedField(field, values) : "-"}</span>
                        </div>
                    </section>
                )}

                <p className="rc-hint">
                    {users.length || ruleSetUsers.length
                        ? `Used by: ${[
                            ...users.map((section) => section.name || "Untitled Metric"),
                            ...ruleSetUsers.map((ruleSet) => `${ruleSet.name || "Untitled Categorization"} (categorization)`),
                        ].join(", ")}.`
                        : isDatePart
                            ? `Not used yet. On the Metrics tab, break a metric down by it, include or leave out some of its values, or filter on it${multiModel ? ` (${modelLabel(field.model)} metrics)` : ""}; it can also be a Full Row Detail column or a Categorization condition.`
                        : isText
                            ? `Not used yet. On the Metrics tab, add it as a column to a Full Row Detail metric${multiModel ? ` on ${modelLabel(field.model)}` : ""}.`
                            : `Not used yet. On the Metrics tab, add it as a column to a Full Row Detail metric or choose it as the Measure of a Count or Percentage metric${multiModel ? ` on ${modelLabel(field.model)}` : ""}.`}
                </p>
            </div>
        );
    }

    function renderFieldItem(field: CalculatedField, index: number) {
        const users = metricsUsingCalculatedField(report, field.id).length;
        return (
            <ReportAccordionItem
                key={field.id}
                id={`calc-${field.id}`}
                eyebrow={`Field ${index + 1}`}
                title={field.name || "Untitled Calculated Field"}
                summary={calculatedFieldEnglish(field) || "Formula not finished"}
                meta={<><StatusIcon ready={isCalculatedFieldReady(report, field)} /><span className="rc-badge">{users} Metric{users === 1 ? "" : "s"}</span></>}
                open={openFieldId === field.id}
                onOpenChange={(nextOpen) => setOpenFieldId(nextOpen ? field.id : "")}
            >
                {renderField(field)}
            </ReportAccordionItem>
        );
    }

    if (!report.models.length) {
        return <section className="rc-block"><h3>Calculated Fields</h3><p className="rc-hint">Add a Data Model on the Setup tab first, then define calculated fields here.</p></section>;
    }

    const orphaned = fields.filter((field) => !report.models.includes(field.model));
    return (
        <section className="rc-block rc-calc-editor">
            <h3>Calculated Fields <span className="rc-badge">Reusable</span></h3>
            <p className="rc-block-hint">Work out a value for each record. A <b>Number</b> field uses number columns, typed numbers, the time between two dates and text read as a number - for example Billed Amount ÷ Trip Miles. A <b>Text</b> field joins and reshapes text - trim, change case, take part of a value, replace, pad, mask or format it - for example Last Name, First Name. A <b>Date Part</b> field takes one part of a date - Day Name, Month Name, Year, Week Of Month, Start Of Week, Hour and more. Once complete, every field is listed as <b>"(Calculated)"</b> among the columns of its data model: break a metric down by it, include or leave out some of its values, group its values, or use it in a filter or a Categorization condition. Add any field to a Full Row Detail metric as a column; a Count or Percentage metric can add up a number field instead of counting rows. Number format and header for the Excel sheet are set on the Excel Layout tab.</p>
            {report.models.map((modelKey) => {
                const groupFields = fields.filter((field) => field.model === modelKey);
                return (
                    <div className={multiModel ? "rc-calc-group" : ""} key={modelKey}>
                        {multiModel && (
                            <div className="rc-calc-group-head">
                                <span className="rc-calc-group-title">{modelLabel(modelKey)}</span>
                                <span className="rc-hint">{groupFields.length} field{groupFields.length === 1 ? "" : "s"} - built from {modelLabel(modelKey)} columns, usable by {modelLabel(modelKey)} metrics only</span>
                            </div>
                        )}
                        {!groupFields.length && <p className="rc-hint">No calculated fields{multiModel ? ` on ${modelLabel(modelKey)}` : ""} yet. Add one below.</p>}
                        {groupFields.length > 0 && <div className="rc-accordion-stack">{groupFields.map((field) => renderFieldItem(field, fields.indexOf(field)))}</div>}
                        <AddActionButton className={multiModel ? "compact" : ""} onClick={() => addField(modelKey)}>
                            {multiModel ? `Add ${modelLabel(modelKey)} Field` : "Add Calculated Field"}
                        </AddActionButton>
                    </div>
                );
            })}
            {orphaned.length > 0 && (
                <div className="rc-calc-group warning">
                    <div className="rc-calc-group-head">
                        <span className="rc-calc-group-title">Data Model Removed From This Report</span>
                        <span className="rc-hint">Choose a data model for these fields, or delete them.</span>
                    </div>
                    <div className="rc-accordion-stack">{orphaned.map((field) => renderFieldItem(field, fields.indexOf(field)))}</div>
                </div>
            )}
        </section>
    );
}
